import usePlacesAutocomplete, {
  getGeocode,
  getDetails,
} from "use-places-autocomplete";
import useOnclickOutside from "react-cool-onclickoutside";
import { emitCustomEvent } from "react-custom-events";
import { useEffect, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";

const defaultTypes = ["address"];

const getZipCode = (geocodeResult) => {
  if (!geocodeResult || !geocodeResult.address_components) {
    return null;
  }

  const zipComponent = geocodeResult.address_components.find((component) =>
    component.types.includes("postal_code")
  );

  return zipComponent ? zipComponent.long_name : "00000";
};

const PlacesAutocomplete = ({
  label,
  pValue,
  setAddressCheck,
  error,
  isAddressSearch = false,
  type,
}) => {
  const { t } = useTranslation();
  const searchPara = isAddressSearch ? defaultTypes : [];
  const inputRef = useRef(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    let timeoutId;
    const checkGoogleMapsLoaded = () => {
      if (window.google && window.google.maps && window.google.maps.places) {
        setIsLoaded(true);
      } else {
        timeoutId = setTimeout(checkGoogleMapsLoaded, 100);
      }
    };

    checkGoogleMapsLoaded();

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, []);

  const {
    ready,
    value,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    requestOptions: {
      // LatAm focus: El Salvador + Central America + key South American countries (API max 5)
      componentRestrictions: { country: ["sv", "gt", "hn", "co", "mx"] },
      types: [...searchPara],
    },
    debounce: 300,
    enabled: isLoaded,
  });

  useEffect(() => {
    setValue(pValue, false);
  }, [pValue, setValue]);

  const ref = useOnclickOutside(() => {
    clearSuggestions();
  });

  const handleInput = (e) => {
    if (setAddressCheck) {
      setAddressCheck(e);
    }
    setValue(e.target.value);
  };

  const handleSelect =
    ({ description, terms }) =>
    () => {
      if (!description) {
        toast.error(t("errors.something-is-went-wrong"));
        return;
      }

      let addressLine = "";

      if (terms && terms.length > 3) {
        for (let i = 0; i < terms.length - 3; i++) {
          addressLine += terms[i].value + (i !== terms.length - 4 ? ", " : "");
        }
      } else {
        addressLine = description.split(",")[0] || description;
      }

      setValue(addressLine, false);
      clearSuggestions();

      getGeocode({ address: description })
        .then(async (results) => {
          if (!results || results.length === 0) {
            throw new Error("ZERO_RESULTS: No geocoding results found");
          }

          try {
            const zipCode = getZipCode(results[0]);

            const parameter = {
              placeId: results[0].place_id,
              fields: ["formatted_phone_number", "name", "website", "types"],
            };
            const details = await getDetails(parameter);

            emitCustomEvent("placeSelected", {
              description: results[0].formatted_address,
              addressLine: addressLine,
              terms: terms || [],
              zipCode: zipCode,
              lat: results[0].geometry.location.lat(),
              lng: results[0].geometry.location.lng(),
              business_number: details.formatted_phone_number,
              business_name: details.name,
              website: details.website,
              categories: details.types,
            });
          } catch (detailsError) {
            toast.error(t("errors.something-is-went-wrong"));
          }
        })
        .catch(() => {
          toast.error(t("errors.something-is-went-wrong"));
        });
    };

  const renderSuggestions = () =>
    data.map((suggestion) => {
      const {
        place_id,
        structured_formatting: { main_text, secondary_text },
        terms,
        description: fullAddress,
      } = suggestion;

      return (
        <li
          className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm border-b border-gray-100 last:border-b-0"
          key={place_id}
          onClick={handleSelect({ description: fullAddress, terms })}
        >
          <span className="font-medium">{main_text}</span>
          <span className="text-gray-500 ml-1">{secondary_text}</span>
        </li>
      );
    });

  return (
    <div ref={ref} className="relative">
      <label className="block font-bold title text-warmgray-900">{label}</label>
      <div
        className={`flex flex-row border w-full focus:outline-none focus-within:border-violet-400 ${
          error ? "border-red-500" : ""
        } ${!ready || !isLoaded ? "bg-gray-50" : ""}`}
      >
        <input
          ref={inputRef}
          value={value}
          onChange={handleInput}
          disabled={!ready || !isLoaded}
          placeholder={label}
          className={`w-full focus:outline-none flex border border-warmgray-300 rounded-md p-2 ${
            !ready || !isLoaded ? "bg-gray-50" : ""
          }`}
          type={type}
        />
      </div>
      {error && (
        <span className="block text-red-500">
          <small>{error}</small>
        </span>
      )}
      {status === "OK" && (
        <ul className="z-[999] absolute bg-white border border-warmgray-300 w-full shadow-md mt-1 rounded-md max-h-60 overflow-y-auto">
          {renderSuggestions()}
        </ul>
      )}
    </div>
  );
};

export default PlacesAutocomplete;
